import React, { createContext, useContext, ReactNode } from 'react';
import { IHumanVerificationConfig } from '../../../types/humanVerification';
import { trackExceptionWithContext } from '../../../services/application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from '../../../services/application-insights/appInsightsConstants';

interface HumanVerificationContextValue extends IHumanVerificationConfig {
  isConfigured: boolean;
}

const HumanVerificationContext = createContext<HumanVerificationContextValue | null>(null);

interface HumanVerificationProviderProps {
  children: ReactNode;
  config: IHumanVerificationConfig;
}

export const HumanVerificationProvider: React.FC<HumanVerificationProviderProps> = ({
  children,
  config,
}) => {
  try {
    const contextValue: HumanVerificationContextValue = {
      ...config,
      isConfigured: !!(config.turnstile?.siteKey || config.recaptcha?.siteKey),
    };

    // Log configuration validation errors
    if (!contextValue.isConfigured) {
      trackExceptionWithContext(new Error('Human verification provider configuration is incomplete'), {
        component: AppInsightsComponents.HUMAN_VERIFICATION_PROVIDER,
        action: 'configuration-incomplete',
        additionalData: {
          errorType: AppInsightsErrorTypes.HUMAN_VERIFICATION_CONFIG_ERROR,
          hasTurnstileConfig: !!config.turnstile,
          hasRecaptchaConfig: !!config.recaptcha,
          hasTurnstileSiteKey: !!config.turnstile?.siteKey,
          hasRecaptchaSiteKey: !!config.recaptcha?.siteKey
        }
      });
    }

    return (
      <HumanVerificationContext.Provider value={contextValue}>
        {children}
      </HumanVerificationContext.Provider>
    );
  } catch (error) {
    trackExceptionWithContext(error as Error, {
      component: AppInsightsComponents.HUMAN_VERIFICATION_PROVIDER,
      action: 'provider-initialization-failed',
      additionalData: {
        errorType: AppInsightsErrorTypes.HUMAN_VERIFICATION_PROVIDER_ERROR,
        hasConfig: !!config
      }
    });
    throw error;
  }
};

export const useIHumanVerificationConfig = (): HumanVerificationContextValue => {
  const context = useContext(HumanVerificationContext);
  
  if (!context) {
    const errorMessage = 'useIHumanVerificationConfig must be used within a HumanVerificationProvider';
    trackExceptionWithContext(new Error(errorMessage), {
      component: AppInsightsComponents.HUMAN_VERIFICATION_PROVIDER,
      action: 'context-not-found',
      additionalData: {
        errorType: AppInsightsErrorTypes.HUMAN_VERIFICATION_PROVIDER_ERROR,
        hookUsage: 'useIHumanVerificationConfig'
      }
    });
    throw new Error(errorMessage);
  }
  
  return context;
};

export default HumanVerificationProvider;